import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useQuery } from '@tanstack/react-query';

import { VisitorProvider } from '../context/VisitorContext';
import { FormContent } from '../components/VisitorForm/FormContent';
import { visitorValidationSchema } from '../validations/visitorValidationSchema';
import { initialValues } from '../constants/visitorConstants';
import dayjs from 'dayjs';
import { visitorService } from '../../../shared/services/visitors/visitorService';
import { useVisitor } from '../context';
import { userService } from '../../../shared/services/users/usersServices';
import { roomService } from '../../../shared/services/rooms/roomsServices';
import { useTenant } from '../../../shared/context/TenantContext';
import { useAuth } from '../../../modules/auth/context/AuthContext';

function VisitorFormContent() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { createVisitor, updateVisitor, error } = useVisitor();
  const [loading, setLoading] = useState(false);
  const { selectedTenant, handleSetSelectedTenant } = useTenant();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';

  const isEditing = Boolean(id);

  // Query para buscar usuários condôminos
  const { data: usersData = [] } = useQuery({
    queryKey: ['users', 'condomino'],
    queryFn: async () => {
      const response = await userService.list({ type_flag: 'condomino' });
      return response.data || [];
    }
  });

  // Query para buscar rooms (áreas)
  const { data: roomsData = [] } = useQuery({
    queryKey: ['rooms'],
    queryFn: async () => {
      const response = await roomService.list();
      return response.data || [];
    }
  });

  // Query para buscar dados do visitor se estiver editando
  const { data: visitor } = useQuery({
    queryKey: ['visitor', id],
    queryFn: () => visitorService.getById(id!),
    enabled: isEditing,
  });

  // Transformar dados para options dos selects
  interface UserOption {
    value: string;
    label: string;
  }

  interface User {
    id: string;
    name: string;
  }

  const userOptions: UserOption[] = usersData.map((user: User) => ({
    value: user.id,
    label: user.name,
  }));

  const areaOptions = roomsData.map(room => ({
    value: room.id,
    label: room.name
  }));

  const getInitialValues = () => {
    if (visitor) {
      return {
        name: visitor.name,
        document: visitor.document_number,
        phone_number: visitor.phone_number,
        tenant_id: visitor.tenant.id,
        user_id: visitor.user.id,
        start_date: visitor.start_date,
        end_date: visitor.end_date,
        area_id: visitor.areas.map((area: { id: string }) => area.id),
        status_flag: visitor.status_flag,
        images: [] as string[],
      };
    }
    return {
      ...initialValues,
      tenant_id: selectedTenant || '',
      start_date: dayjs().toISOString(),
      end_date: dayjs().add(1, 'day').toISOString(),
    };
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      const submitData = {
        ...values,
        tenant_id: selectedTenant,
        start_date: dayjs(values.start_date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        end_date: dayjs(values.end_date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        area_id: Array.isArray(values.area_id) ? values.area_id : [values.area_id],
      };

      console.log('Dados enviados:', submitData);

      if (isEditing) {
        await updateVisitor(id!, submitData);
      } else {
        await createVisitor(submitData);
      }
    } catch (error) {
      console.error('Erro ao salvar visitante:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {isEditing ? 'Editar Visitante' : 'Novo Visitante'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={visitorValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <FormContent
                values={values}
                errors={errors}
                touched={touched}
                isSubmitting={isSubmitting || loading}
                setFieldValue={setFieldValue}
                userOptions={userOptions}
                areaOptions={areaOptions}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || loading}
                >
                  {isEditing ? 'Atualizar' : 'Criar'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/visitors')}
                  disabled={isSubmitting || loading}
                >
                  Cancelar
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
}

export default function VisitorFormPage() {
  return (
    <VisitorProvider>
      <VisitorFormContent />
    </VisitorProvider>
  );
}